<template>
  <div :class="!isModal ? 'pa-6' : ''">
    <!-- Non-modal wrapper -->
    <v-card v-if="!isModal" class="rounded-lg border-soft">
      <v-toolbar color="var(--surface2)" elevation="0">
        <v-toolbar-title class="panel-title">Profile Settings</v-toolbar-title>
      </v-toolbar>
      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <v-form ref="profileFormPage" v-if="dataLoaded">
          <!-- Social Profile Form Content -->
          <v-row>
            <v-col cols="12" class="d-flex justify-center mb-6">
              <input
                type="file"
                ref="profile_pic_page"
                @change="changeFile($event)"
                hidden
              />
              <v-badge
                bordered
                bottom
                overlap
                offset-x="28"
                offset-y="28"
                color="var(--brand)"
              >
                <template v-slot:badge>
                  <v-icon
                    small
                    dark
                    @click="openFileExplorer"
                    style="cursor: pointer"
                    >mdi-pencil</v-icon
                  >
                </template>
                <v-avatar
                  size="140"
                  class="elevation-4 border-soft"
                  :style="{ background: primaryColor + '20' }"
                >
                  <img
                    v-if="previewImage || user.logo_url"
                    :src="
                      previewImage || API_BASE_URL + '/uploads/' + user.logo_url
                    "
                    style="object-fit: contain"
                  />
                  <span
                    v-else
                    class="text-h2 font-weight-bold"
                    :style="{ color: primaryColor }"
                    >{{
                      user.name ? user.name.charAt(0).toUpperCase() : "A"
                    }}</span
                  >
                </v-avatar>
              </v-badge>
            </v-col>

            <v-col cols="12">
              <v-row dense>
                <v-col cols="12">
                  <PremiumInput
                    v-model="user.name"
                    label="Society Name"
                    placeholder="Enter society name"
                    icon="mdi-office-building-outline"
                    v-validate="'required'"
                    name="name"
                    :error="errors.first('name') || serverSideErrors.name"
                  />
                </v-col>
                <v-col cols="12">
                  <PremiumInput
                    v-model="user.address"
                    label="Society Address"
                    placeholder="Enter full address"
                    icon="mdi-map-marker-outline"
                    multiline
                    :rows="2"
                    v-validate="'required'"
                    name="address"
                    :error="errors.first('address') || serverSideErrors.address"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row dense class="mt-4">
            <v-col cols="12">
              <PremiumInput
                v-model="user.contact_person_name"
                label="Contact Person"
                icon="mdi-account-outline"
                v-validate="'required'"
                name="contact_person_name"
                :error="
                  errors.first('contact_person_name') ||
                  serverSideErrors.contact_person_name
                "
              />
            </v-col>
            <v-col cols="12">
              <PremiumInput
                v-model="user.current_balance"
                label="Current Balance"
                icon="mdi-currency-inr"
                type="number"
              />
            </v-col>
            <v-col cols="12">
              <div class="premium-country-wrap-full">
                <label class="premium-label">Country</label>
                <vue-country-code
                  @onSelect="onSelect"
                  class="custom-country-code-select-full"
                  :defaultCountry="user.countryIso || 'IN'"
                />
              </div>
            </v-col>
            <v-col cols="12">
              <PremiumInput
                v-model="user.contact_person_number"
                label="Mobile Number"
                v-validate="'required'"
                name="number"
                :error="
                  errors.first('number') ||
                  serverSideErrors.contact_person_number
                "
              />
            </v-col>
            <v-col cols="12">
              <PremiumInput
                v-model="user.contact_person_email"
                label="Email Address"
                icon="mdi-email-outline"
                v-validate="'required|email'"
                name="email"
                :error="
                  errors.first('email') || serverSideErrors.contact_person_email
                "
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-6 pt-0">
        <v-btn
          depressed
          class="btn-premium-primary"
          @click="updateProfile"
          :loading="loading"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Modal content (naked form) -->
    <div v-else>
      <v-form ref="profileForm" v-if="dataLoaded">
        <v-row>
          <v-col cols="12" class="d-flex justify-center mb-8">
            <input
              type="file"
              @change="changeFile($event)"
              ref="profile_pic"
              hidden
            />
            <div class="avatar-uploader-container">
              <v-avatar
                size="140"
                class="elevation-3"
                :style="{
                  background: primaryColor + '20',
                  border: '2px solid white',
                }"
              >
                <img
                  v-if="previewImage || user.logo_url"
                  :src="
                    previewImage || API_BASE_URL + '/uploads/' + user.logo_url
                  "
                  style="object-fit: contain"
                />
                <span
                  v-else
                  class="text-h2 font-weight-bold"
                  :style="{ color: primaryColor }"
                  >{{
                    user.name ? user.name.charAt(0).toUpperCase() : "A"
                  }}</span
                >
              </v-avatar>
              <v-btn
                fab
                small
                depressed
                @click="openFileExplorer"
                class="avatar-edit-btn"
                color="var(--brand)"
              >
                <v-icon small color="white">mdi-pencil</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12">
            <PremiumInput
              v-model="user.name"
              label="Society Name"
              icon="mdi-office-building-outline"
              v-validate="'required'"
              name="name"
              :error="errors.first('name') || serverSideErrors.name"
            />
          </v-col>
          <v-col cols="12">
            <PremiumInput
              v-model="user.address"
              label="Society Address"
              placeholder="Enter full address"
              icon="mdi-map-marker-outline"
              multiline
              :rows="2"
              v-validate="'required'"
              name="address"
              :error="errors.first('address') || serverSideErrors.address"
            />
          </v-col>
          <v-col cols="12">
            <PremiumInput
              v-model="user.contact_person_name"
              label="Contact Person"
              icon="mdi-account-outline"
              v-validate="'required'"
              name="contact_person_name"
              :error="
                errors.first('contact_person_name') ||
                serverSideErrors.contact_person_name
              "
            />
          </v-col>
          <v-col cols="12">
            <PremiumInput
              v-model="user.current_balance"
              label="Current Balance"
              icon="mdi-currency-inr"
              type="number"
            />
          </v-col>
          <v-col cols="12">
            <div class="premium-field-label mb-2">Mobile Number</div>
            <div class="phone-row">
              <div class="country-code-wrap">
                <vue-country-code
                  @onSelect="onSelect"
                  class="custom-country-code-select-full"
                  :defaultCountry="user.countryIso || 'IN'"
                />
              </div>
              <PremiumInput
                v-model="user.contact_person_number"
                label=""
                style="flex: 1"
                v-validate="'required'"
                name="number"
                :error="
                  errors.first('number') ||
                  serverSideErrors.contact_person_number
                "
              />
            </div>
          </v-col>
          <v-col cols="12">
            <PremiumInput
              v-model="user.contact_person_email"
              label="Email Address"
              icon="mdi-email-outline"
              v-validate="'required|email'"
              name="email"
              :error="
                errors.first('email') || serverSideErrors.contact_person_email
              "
            />
          </v-col>
        </v-row>
      </v-form>
    </div>
    <!-- Image Cropper Dialog -->
    <v-dialog v-model="cropDialog" max-width="540" persistent>
      <v-card class="cropper-card">
        <!-- Header -->
        <div class="cropper-header">
          <span class="cropper-title">Crop Society Logo</span>
          <v-btn icon small @click="cropDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <!-- Crop Area -->
        <div class="round-cropper">
          <vue-cropper
            ref="cropper"
            :src="cropSrc"
            :aspect-ratio="1"
            :view-mode="1"
            drag-mode="move"
            :auto-crop-area="0.85"
            :min-container-width="490"
            :min-container-height="340"
            style="max-height:340px"
          />
        </div>

        <!-- Action Toolbar: Zoom / Rotate / Flip -->
        <div class="cropper-toolbar">
          <v-btn icon small title="Zoom Out" @click="$refs.cropper.relativeZoom(-0.1)"><v-icon>mdi-magnify-minus-outline</v-icon></v-btn>
          <v-btn icon small title="Zoom In"  @click="$refs.cropper.relativeZoom(0.1)"><v-icon>mdi-magnify-plus-outline</v-icon></v-btn>
          <v-btn icon small title="Rotate Left"  @click="$refs.cropper.rotate(-45)"><v-icon>mdi-rotate-left</v-icon></v-btn>
          <v-btn icon small title="Rotate Right" @click="$refs.cropper.rotate(45)"><v-icon>mdi-rotate-right</v-icon></v-btn>
          <v-btn icon small title="Flip H" @click="$refs.cropper.scaleX($refs.cropper.getData().scaleX === -1 ? 1 : -1)"><v-icon>mdi-flip-horizontal</v-icon></v-btn>
          <v-btn icon small title="Flip V" @click="$refs.cropper.scaleY($refs.cropper.getData().scaleY === -1 ? 1 : -1)"><v-icon>mdi-flip-vertical</v-icon></v-btn>
        </div>

        <!-- Footer Buttons -->
        <div class="cropper-footer">
          <v-btn text class="cropper-cancel-btn" @click="cropDialog = false">Cancel</v-btn>
          <v-btn depressed class="cropper-apply-btn" @click="cropImage">
            <v-icon small left>mdi-crop</v-icon>Apply Crop
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PremiumInput from "../common/PremiumInput.vue";
import VueCropper from "vue-cropperjs";
import "cropperjs/dist/cropper.css";

export default {
  name: "SocietyAdminProfile",

  components: { PremiumInput, VueCropper },

  props: {
    isModal: { type: Boolean, default: false },
  },

  data() {
    return {
      loading: false,
      cropDialog: false,
      cropSrc: "",
      user: {
        name: "",
        address: "",
        contact_person_name: "",
        contact_person_number: "",
        contact_person_email: "",
        logo_url: "",
      current_balance: 0,
        country_code: "",
        countryIso: "",
      },
      serverSideErrors: {},
      profile_pic: {},
      previewImage: null,
      dataLoaded: false,
    };
  },
  created() {
    this.getSocietyDetail();
  },

  computed: {
    ...mapState("theme", ["primaryColor"]),
  },

  watch: {
    loading(val) {
      this.$emit("loading", val);
    },
  },

  methods: {
    getSocietyDetail() {
      let _self = this;
      _self.$axios.get("/societyadmin/societyProfile").then((response) => {
        let data = response.data;

        if (data.status) {
          _self.user = data.society;

          _self.user.countryIso = data.society.countryIso;
          console.log(_self.user.countryIso);
          localStorage.setItem("flag", JSON.stringify(_self.user.countryIso));
        } else {
          if (data.errors) {
            _self.serverSideErrors = {};

            for (let i = 0; i < data.errors.length; i++) {
              _self.serverSideErrors[data.errors[i].field] =
                data.errors[i].message;
            }
          } else {
            this.setAlert({
              showAlert: true,
              variant: "red accent-3",
              timeout: "2000",
              message: data.message,
            });
          }
        }
        _self.dataLoaded = true;
      }).catch(() => {
        _self.dataLoaded = true;
      });
    },
    onSelect(event) {
      this.user.country_code = "+" + event.dialCode;
      this.user.countryIso = event.iso2;
    },
    ...mapMutations("alert", ["setAlert"]),
    updateProfile() {
      let _self = this;
      this.$validator.validateAll().then((result) => {
        if (result) {
          _self.loading = true;
          let formData = new FormData();

          formData.append("name", this.user.name);
          console.log(this.user.name);
          formData.append("address", this.user.address);
          formData.append("contact_person_name", this.user.contact_person_name);
          formData.append("current_balance", this.user.current_balance || 0);
          formData.append(
            "contact_person_number",
            this.user.contact_person_number,
          );
          formData.append(
            "contact_person_email",
            this.user.contact_person_email,
          );
          formData.append("country_code", this.user.country_code);
          formData.append("countryIso", this.user.countryIso);
          formData.append("logo_url", this.profile_pic);

          _self.$axios
            .post("/societyadmin/societyProfile", formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            })
            .then((response) => {
              let data = response.data;

              _self.loading = false;
              _self.serverSideErrors = {};

              if (data.status) {
                this.user = data.user;
                // console.log(this.user);
                this.profile_pic = {};

                data.user.role = "societyadmin";
                data.user.token = this.user.token;
                localStorage.setItem("user", JSON.stringify(data.user));

                // let user = localStorage.getItem("user");
                // console.log(user.countryIso)
                if (data.user.logo_url) {
                  _self.$emit("changeProfilePic");
                }

                this.setAlert({
                  showAlert: true,
                  message: data.message,
                  variant: "green accent-4",
                  timeout: 2000,
                });
                if (this.isModal) {
                  this.$emit("close");
                }
              } else {
                if (!data.errors) {
                  this.setAlert({
                    showAlert: true,
                    message: data.message,
                    variant: "red accent-3",
                    timeout: 2000,
                  });
                } else {
                  for (let i = 0; i < data.errors.length; i++) {
                    this.serverSideErrors[data.errors[i].field] =
                      data.errors[i].message;
                  }
                }
              }
            });
        }
      });
    },
    openFileExplorer() {
      // Support both page mode and modal mode refs
      const ref = this.$refs.profile_pic_page || this.$refs.profile_pic;
      if (ref) ref.click();
    },
    changeFile(e) {
      const file = e.target.files[0];
      if (!file) return;
      // Reset input so same file can be re-selected
      e.target.value = "";
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.cropSrc = ev.target.result;
        this.cropDialog = true;
        this.$nextTick(() => {
          if (this.$refs.cropper) {
            this.$refs.cropper.replace(this.cropSrc);
          }
        });
      };
      reader.readAsDataURL(file);
    },
    cropImage() {
      const canvas = this.$refs.cropper.getCroppedCanvas({
        width: 400,
        height: 400,
        imageSmoothingQuality: "high",
      });
      this.previewImage = canvas.toDataURL("image/jpeg", 0.9);
      canvas.toBlob(
        (blob) => {
          this.profile_pic = new File([blob], "logo.jpg", { type: "image/jpeg" });
          this.cropDialog = false;
        },
        "image/jpeg",
        0.9
      );
    },
  },
};
</script>

<style scoped>
.font-30 {
  font-size: 30px !important;
}
/* make the icon clickable in badge slot */
.edit-icon {
  z-index: 1000;
}
.custom-country-code-select {
  width: 72px;
  height: 40px;
  border: 1px solid var(--border, rgba(0, 0, 0, 0.1)) !important;
  background-color: var(--surface2, #fafafa);
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.premium-country-wrap-full {
  width: 100%;
}
.custom-country-code-select-full {
  width: 100%;
  height: 40px;
  border: 1px solid var(--border, rgba(0, 0, 0, 0.1)) !important;
  background-color: var(--surface2) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}
::v-deep .custom-country-code-select-full .vue-country-select {
  width: 100%;
  height: 100%;
  border: none !important;
}
::v-deep .custom-country-code-select-full .dropdown-list {
  width: 100% !important;
}
.theme--dark .custom-country-code-select-full {
  background-color: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.custom-country-code-select:hover,
.custom-country-code-select-full:hover {
  border-color: var(--brand) !important;
}

.premium-country-wrap {
  display: flex;
  flex-direction: column;
}

.premium-label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  padding-left: 4px;
}

.vue-country-select:focus-within {
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.12) !important;
  border-color: var(--brand) !important;
}

.avatar-uploader-container {
  position: relative;
  display: inline-block;
}

.avatar-edit-btn {
  position: absolute !important;
  bottom: 2px !important;
  right: 2px !important;
  width: 38px !important;
  height: 38px !important;
  border: 4px solid white !important;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.12) !important;
  transition: all 0.2s ease !important;
}

.avatar-edit-btn:hover {
  transform: scale(1.1);
}

.theme--dark .avatar-edit-btn {
  border-color: #111827 !important;
}

/* ── Phone Row (country code + mobile inline) ── */
.premium-field-label {
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-left: 4px;
}

.phone-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.country-code-wrap {
  flex-shrink: 0;
  width: 90px;
}

/* dropdown opens upward when near modal bottom */
::v-deep .custom-country-code-select-full .dropdown-list {
  width: 260px !important;
  top: auto !important;
  bottom: 100% !important;
  margin-bottom: 4px;
  max-height: 200px;
  overflow-y: auto;
}

/* ── Round Cropper ── */
.round-cropper {
  background: #fff !important;
}
.round-cropper ::v-deep .cropper-bg {
  background-image: none !important;
  background-color: #fff !important;
}
.round-cropper ::v-deep .cropper-modal {
  background-color: #fff !important;
  opacity: 0.75 !important;
}
.round-cropper ::v-deep .cropper-crop-box,
.round-cropper ::v-deep .cropper-view-box {
  border-radius: 50%;
}
.round-cropper ::v-deep .cropper-view-box {
  outline: 3px solid var(--primary, #6366f1) !important;
  outline-offset: -1px;
}
.round-cropper ::v-deep .cropper-face {
  background-color: inherit !important;
}
.round-cropper ::v-deep .cropper-dashed {
  display: none;
}

/* ── Cropper Dialog Design ── */
.cropper-card {
  border-radius: 16px !important;
  overflow: hidden;
}
.cropper-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  background: #fff;
}
.cropper-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a202c;
}
.cropper-toolbar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 12px 16px;
  background: #f8fafc;
  border-top: 1px solid #eaecf0;
  border-bottom: 1px solid #eaecf0;
}
.cropper-toolbar .v-btn { color: #4a5568 !important; }
.cropper-toolbar .v-btn:hover { background: #e2e8f0 !important; color: #1a202c !important; }
.cropper-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #fff;
}
.cropper-cancel-btn {
  color: #718096 !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}
.cropper-apply-btn {
  background: var(--brand, var(--primary, #f97316)) !important;
  color: #fff !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  padding: 0 20px !important;
  height: 40px !important;
  box-shadow: 0 4px 14px var(--primary-shadow, rgba(249,115,22,0.25)) !important;
}
.cropper-apply-btn:hover { opacity: 0.9; }
</style>
